import React, { useEffect, useState, useRef } from "react";
import useEntreprises from "../../hooks/entreprise/useEntrepries";
import UsersServices from "../../services/utilisateur/UsersService";
import useUsers from "../../hooks/utilisateur/useUsers";
import { defaultImage } from "../../utils/ImageEnBase46";
import { FiSearch, FiFilter, FiX, FiPlus } from "react-icons/fi";
import "../../style/alertPage.css";
import Loader from "../components/loader";

function ConfigurationAlert() {
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const { entreprises, fetchEntreprises } = useEntreprises();
  const [entrepriseSelected, setEntrepriseSelected] = useState("");
  const [superviseursSelected, setSuperviseursSelected] = useState([]);
  const { users, setUsers } = useUsers();
  const userData = JSON.parse(localStorage.getItem("user"));
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [alertConfig, setAlertConfig] = useState([]);
  const [alertFilter, setAlertFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [emailGroup, setEmailGroup] = useState("");
  const [activePopover, setActivePopover] = useState(null);
  const popoverRef = useRef(null);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const [loading, setLoading] = useState(false);

  const [editingUserId, setEditingUserId] = useState(null);
  const [newEmail, setNewEmail] = useState("");

  const isAdmin = userData?.role === 1;
  const isSuperviseur = userData?.role === 2;

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 3000);
  };

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const response = await UsersServices.getAll();
      const usersRole3 = response.data.data.filter((user) => user.role === 3);
      setUsers(usersRole3);
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllUsers();
    fetchEntreprises();
  }, []);

  useEffect(() => {
    if (!userData || !isSuperviseur) return;
    if (entreprises.length === 0) return;

    const entId = userData.entreprise?._id || userData.entreprise;
    setEntrepriseSelected(entId);
    setSuperviseursSelected([userData._id]);
  }, [entreprises]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setActivePopover(null);
      }
    };

    if (activePopover) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activePopover]);

  const activeUsers = users
    .filter((user) => user.active == 1 && user.role == 3)
    .filter((user) => {
      if (companyFilter === "all") return true;
      return user?.entreprise?._id === companyFilter;
    })
    .filter((user) => {
      if (isAdmin && !entrepriseSelected) return true;

      const isFromEntreprise = user?.entreprise?._id === entrepriseSelected;
      const isSuperviseurOK =
        superviseursSelected.length === 0 ||
        user?.gerant?.some((gId) => superviseursSelected.includes(gId));
      return isFromEntreprise && isSuperviseurOK;
    })
    .filter((user) => {
      if (alertFilter.length === 0) return true;
      return alertFilter.some((alert) => user?.alerActive?.includes(alert));
    })
    .filter((user) => {
      const fullText =
        `${user.nom} ${user.prenom} ${user.email} ${user.entreprise?.nom || ""}`.toLowerCase();
      return fullText.includes(searchTerm.toLowerCase());
    })
    .filter((user) => {
      if (roleFilter === "all") return true;
      return user.role === Number(roleFilter);
    });

  const totalPages = Math.ceil(activeUsers.length / usersPerPage);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = activeUsers.slice(indexOfFirstUser, indexOfLastUser);

  const handleUsersPerPageChange = (e) => {
    setUsersPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const toggleAlertConfig = (alertId, checked) => {
    if (checked) {
      setAlertConfig([...alertConfig, alertId]);
    } else {
      setAlertConfig(alertConfig.filter((a) => a !== alertId));
    }
  };

  const removeAlertFromUser = async (userId, alertType) => {
    try {
      await UsersServices.removeUserAlert({ userId, alert: alertType });
      setUsers((prev) =>
        prev.map((u) =>
          u._id === userId
            ? { ...u, alerActive: u.alerActive.filter((a) => a !== alertType) }
            : u,
        ),
      );
    } catch (error) {
      console.error("Erreur suppression alerte", error);
    }
  };

  const removeEmail = async (userId, email) => {
    try {
      await UsersServices.removeNotifierEmail({ userId, email });
      setUsers((prev) =>
        prev.map((u) =>
          u._id === userId
            ? {
                ...u,
                emailNotification: {
                  ...u.emailNotification,
                  emails: u.emailNotification.emails.filter((e) => e !== email),
                },
              }
            : u,
        ),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setCompanyFilter("all");
    setAlertFilter([]);
    setRoleFilter("all");
    if (isAdmin) setEntrepriseSelected("");
  };

  const toggleAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(activeUsers.map((u) => u._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelect = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const getAlertClass = (type) => {
    switch (type) {
      case 1: return "badge-danger";
      case 2: return "badge-warning";
      case 3: return "badge-info";
      case 4: return "badge-success";
      case 5: return "badge-dark";
      default: return "badge-secondary";
    }
  };

  const getAlertLabel = (type) => {
    switch (type) {
      case 1: return "Inactivité";
      case 2: return "Pas de connexion";
      case 3: return "Mot clé";
      case 4: return "Installation";
      case 5: return "Navigation interdite";
      default: return "Inconnu";
    }
  };

  const handleAddEmailSubmit = async (userId) => {
    if (!newEmail || !newEmail.includes("@")) {
      showToast("Veuillez entrer un email valide", "danger");
      return;
    }

    try {
      await UsersServices.addNotifierEmail({ userId, email: newEmail });
      setUsers((prev) =>
        prev.map((u) =>
          u._id === userId
            ? {
                ...u,
                emailNotification: {
                  ...u.emailNotification,
                  emails: [...(u.emailNotification?.emails || []), newEmail],
                },
              }
            : u,
        ),
      );
      showToast("Email ajouté avec succès !");
      setEditingUserId(null);
      setNewEmail("");
    } catch (error) {
      console.error(error);
      showToast("Erreur lors de l'ajout", "danger");
    }
  };

  const handleGroupNotifier = async (value) => {
    if (selectedUsers.length === 0) return;

    try {
      await UsersServices.updateNotifierMany({ users: selectedUsers, value });
      setUsers((prev) =>
        prev.map((u) =>
          selectedUsers.includes(u._id) ? { ...u, notifierAdmin: value } : u,
        ),
      );
      const action = value === 1 ? "activées" : "désactivées";
      showToast(`Notifications admin ${action} pour ${selectedUsers.length} postes.`);
    } catch (error) {
      console.error(error);
      showToast("Erreur lors de la mise à jour des notifications", "danger");
    }
  };

  const applyConfiguration = async (mode = "add") => {
    if (selectedUsers.length === 0) {
      showToast("Veuillez sélectionner au moins un poste", "danger");
      return;
    }

    if (alertConfig.length === 0) {
      showToast("Sélectionnez au moins une alerte dans la liste", "warning");
      return;
    }

    try {
      if (mode === "add") {
        await UsersServices.updateManyUserAlerts({ users: selectedUsers, alerts: alertConfig });
        showToast(`${alertConfig.length} alerte(s) ajoutée(s) avec succès !`);
      } else {
        await UsersServices.removeManyUserAlerts({ users: selectedUsers, alerts: alertConfig });
        showToast(`${alertConfig.length} alerte(s) retirée(s) avec succès !`, "info");
      }

      setAlertConfig([]);
      await fetchAllUsers();
    } catch (error) {
      console.error(error);
      showToast("Erreur lors de la modification des alertes", "danger");
    }
  };

  const handleEmailsGroup = async (mode = "add") => {
    if (selectedUsers.length === 0) {
      showToast("Sélectionnez d'abord des postes", "warning");
      return;
    }

    if (!emailGroup.trim()) {
      showToast("Entrez au moins un email", "warning");
      return;
    }

    const emails = emailGroup.split(",").map((e) => e.trim()).filter((e) => e !== "");

    try {
      if (mode === "add") {
        await UsersServices.addEmailsMany({ users: selectedUsers, emails });
        showToast(`${emails.length} email(s) ajouté(s) aux postes sélectionnés`);
      } else {
        await UsersServices.removeEmailsMany({ users: selectedUsers, emails });
        showToast(`${emails.length} email(s) retiré(s) des postes sélectionnés`, "info");
      }

      setEmailGroup("");
      setAlertConfig([]);
      fetchAllUsers();
    } catch (error) {
      console.error(error);
      showToast("Erreur lors de la modification des emails", "danger");
    }
  };

  const isEmailGroupValid = () => {
    if (!emailGroup.trim()) return false;

    const emails = emailGroup.split(",").map((e) => e.trim()).filter((e) => e !== "");
    if (emails.length === 0) return false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.every((email) => emailRegex.test(email));
  };

  return (
    <>
      <div
        className="toast-container position-fixed bottom-0 end-0 p-3"
        style={{ zIndex: 2000, right: "0" }}
      >
        <div
          className={`toast align-items-center text-white bg-${toast.type} border-0 ${toast.show ? "show" : "hide"} p-3`}
          role="alert"
        >
          <div className="d-flex">
            <div className="toast-body">
              {toast.type === "success" ? "✅ " : "❌ "}
              {toast.message}
            </div>
            <button
              type="button"
              className="btn-close btn-close-white me-2 m-auto"
              onClick={() => setToast((prev) => ({ ...prev, show: false }))}
            ></button>
          </div>
        </div>
      </div>

      <div className="container-fluid p-4">
        <header className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="fw-bold">Gestion des Alertes</h2>
            <p className="text-muted">
              Configurez les notifications pour vos postes clients
            </p>
          </div>
          <div className="stats_summary d-flex gap-3">
            <div className="stat_card">
              <strong>{users.length}</strong> Postes
            </div>
            <div className="stat_card">
              <strong>{selectedUsers.length}</strong> Sélectionnés
            </div>
          </div>
        </header>

        <section className="filter_bar shadow-sm mb-4">
          <div className="search_group">
            <FiSearch className="search_icon" />
            <input
              type="text"
              placeholder="Rechercher un nom ou un poste..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter_actions">
            {isAdmin && (
              <select
                className="custom_select form-select"
                value={entrepriseSelected}
                onChange={(e) => setEntrepriseSelected(e.target.value)}
              >
                <option value="">— Toutes les entreprises —</option>
                {entreprises.map((ent) => (
                  <option key={ent._id} value={ent._id}>{ent.nom}</option>
                ))}
              </select>
            )}

            {isSuperviseur && (
              <input
                type="text"
                className="form-control"
                value={
                  entreprises.find((e) => e._id === (entrepriseSelected?._id || entrepriseSelected))
                    ?.nom || "Chargement..."
                }
                disabled
                style={{ background: "#f5f5f5", cursor: "not-allowed", maxWidth: 200 }}
              />
            )}

            <button className="btn_reset" onClick={resetFilters}>
              <span className="d-none d-md-inline">Réinitialiser</span>
              <span className="d-inline d-md-none">Reset</span>
            </button>
          </div>
        </section>

        <div className="row">
          {selectedUsers.length > 0 && (
            <div className="col-lg-4 animate-slide-in">
              <div className="card border-0 shadow-lg sticky-top" style={{ top: "20px", borderRadius: "15px" }}>
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold m-0">Actions groupées</h6>
                    <span className="badge bg-primary-soft text-primary rounded-pill px-3">
                      {selectedUsers.length} sélectionnés
                    </span>
                    <button
                      className="btn btn-outline-danger ms-2"
                      onClick={() => setSelectedUsers([])}
                      style={{
                        display: "flex",
                        border: "none",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0.25rem 0.5rem",
                      }}
                    >
                      <FiX />
                    </button>
                  </div>

                  <hr className="text-muted opacity-25" />

                  <div className="alert-config-box p-3 bg-light rounded-3 mb-4">
                    <label className="small fw-bold text-uppercase mb-2 d-block text-muted" style={{ fontSize: "10px" }}>
                      Configuration des Alertes
                    </label>

                    <div className="grid-2-columns gap-2 mb-3">
                      {[1, 2, 3, 4, 5].map((id) => (
                        <div
                          key={id}
                          className={`alert-tile ${alertConfig.includes(id) ? "active" : ""}`}
                          onClick={() => toggleAlertConfig(id, !alertConfig.includes(id))}
                        >
                          <span>{getAlertLabel(id)}</span>
                        </div>
                      ))}
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-primary flex-grow-1 shadow-sm"
                        onClick={() => applyConfiguration("add")}
                        disabled={alertConfig.length === 0}
                      >
                        Activer
                      </button>
                      <button
                        className="btn btn-outline-danger flex-grow-1 shadow-sm ml-2"
                        onClick={() => applyConfiguration("remove")}
                        disabled={alertConfig.length === 0}
                      >
                        Retirer
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="small fw-bold text-uppercase mb-2 d-block text-muted" style={{ fontSize: "10px" }}>
                      Statut des Notifications Admin
                    </label>
                    <div className="d-flex gap-2">
                      <button className="btn btn-outline-success flex-grow-1 btn-sm" onClick={() => handleGroupNotifier(1)}>
                        Activer
                      </button>
                      <button className="btn btn-outline-secondary flex-grow-1 btn-sm ml-2" onClick={() => handleGroupNotifier(0)}>
                        Désactiver
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="small fw-bold text-uppercase mb-2 d-block text-muted" style={{ fontSize: "10px" }}>
                      Emails de notification (Groupe)
                    </label>

                    <div className="mb-2">
                      <input
                        type="text"
                        className={`form-control form-control-sm shadow-sm ${emailGroup && !isEmailGroupValid() ? "is-invalid" : ""}`}
                        placeholder="email1@test.com, email2@test.com..."
                        value={emailGroup}
                        onChange={(e) => setEmailGroup(e.target.value)}
                        style={{ borderRadius: "8px" }}
                      />
                      <div className="form-text" style={{ fontSize: "10px" }}>
                        {emailGroup && !isEmailGroupValid() ? (
                          <span className="text-danger fw-bold">Format d'email invalide détecté</span>
                        ) : (
                          "Séparez les adresses par des virgules."
                        )}
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-dark flex-grow-1 btn-sm shadow-sm"
                        onClick={() => handleEmailsGroup("add")}
                        disabled={!isEmailGroupValid() || selectedUsers.length === 0}
                      >
                        <FiPlus className="me-1" /> Ajouter
                      </button>
                      <button
                        className="btn btn-outline-danger flex-grow-1 btn-sm shadow-sm ml-2"
                        onClick={() => handleEmailsGroup("remove")}
                        disabled={!isEmailGroupValid() || selectedUsers.length === 0}
                      >
                        <FiX className="me-1" /> Retirer
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={selectedUsers.length > 0 ? "col-lg-8" : "col-lg-12"}>
            <div className="table_card shadow-sm bg-white rounded-3">
              <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                <div className="text-muted small">
                  Affichage de <strong>{activeUsers.length === 0 ? 0 : indexOfFirstUser + 1}</strong> à{" "}
                  <strong>{Math.min(indexOfLastUser, activeUsers.length)}</strong> sur{" "}
                  <strong>{activeUsers.length}</strong> postes
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label className="small text-muted mb-0">Lignes :</label>
                  <select className="custom_select form-select" value={usersPerPage} onChange={handleUsersPerPageChange}>
                    <option value="2">2</option>
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </div>

              <table className="table custom_table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={
                          currentUsers.length > 0 &&
                          currentUsers.every((u) => selectedUsers.includes(u._id))
                        }
                        onChange={toggleAll}
                      />
                    </th>
                    <th>Profil</th>
                    <th>Utilisateur</th>
                    <th>Status Alertes</th>
                    <th>Emails notifier</th>
                    <th className="text-center">Alertes</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5">
                        <Loader />
                      </td>
                    </tr>
                  ) : currentUsers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        Aucun utilisateur trouvé
                      </td>
                    </tr>
                  ) : (
                    currentUsers.map((user) => (
                      <tr key={user._id}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check"
                            checked={selectedUsers.includes(user._id)}
                            onChange={() => handleSelect(user._id)}
                          />
                        </td>
                        <td className="text-center">
                          <img
                            src={user?.profil?.startsWith("data:") ? user?.profil : defaultImage}
                            alt="Profil"
                            className="rounded-circle shadow-sm"
                            style={{ width: "35px", height: "35px", objectFit: "cover" }}
                          />
                        </td>
                        <td>
                          <div className="fw-bold">{user.nom}</div>
                          <div className="text-muted small">{user.prenom}</div>
                        </td>
                        <td>
                          <label>
                            <input
                              type="checkbox"
                              checked={user.notifierAdmin === 1}
                              onChange={async (e) => {
                                const value = e.target.checked ? 1 : 0;
                                setUsers((prev) =>
                                  prev.map((u) => (u._id === user._id ? { ...u, notifierAdmin: value } : u)),
                                );
                                try {
                                  await UsersServices.updateNotifier({ userId: user._id, value });
                                } catch (error) {
                                  console.error(error);
                                  setUsers((prev) =>
                                    prev.map((u) =>
                                      u._id === user._id
                                        ? { ...u, notifierAdmin: value === 1 ? 0 : 1 }
                                        : u,
                                    ),
                                  );
                                }
                              }}
                            />
                            Notifier admin{" "}
                          </label>
                        </td>
                        <td>
                          <div className="email-container-compact d-flex align-items-center gap-2">
                            {user.emailNotification?.emails?.length > 0 && (
                              <span className="badge bg-light text-dark border d-flex align-items-center py-2">
                                <span className="email-main text-truncate" style={{ maxWidth: "120px" }}>
                                  <FiX
                                    className="text-danger"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => removeEmail(user._id, user.emailNotification.emails[0])}
                                  />
                                  {user.emailNotification.emails[0]}
                                </span>
                                {user.emailNotification.emails.length > 1 && (
                                  <div
                                    className="popover-wrapper"
                                    ref={user._id === activePopover ? popoverRef : null}
                                  >
                                    <span
                                      className="badge-more"
                                      onClick={() =>
                                        setActivePopover(activePopover === user._id ? null : user._id)
                                      }
                                    >
                                      +{user.emailNotification.emails.length - 1}
                                    </span>

                                    {activePopover === user._id && (
                                      <div className="mini-mail-popover shadow-lg">
                                        <div className="popover-header">Autres emails</div>
                                        <div className="popover-body">
                                          {user.emailNotification.emails.map((email, idx) => (
                                            <div key={idx} className="popover-item">
                                              <span>{email}</span>
                                              <FiX
                                                className="text-danger cursor-pointer"
                                                onClick={() => removeEmail(user._id, email)}
                                              />
                                            </div>
                                          ))}
                                        </div>
                                        <div className="popover-arrow"></div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </span>
                            )}
                            {editingUserId === user._id ? (
                              <div className="input-group input-group-sm" style={{ width: "200px" }}>
                                <input
                                  type="email"
                                  className="form-control form-control-sm"
                                  placeholder="ex@test.com"
                                  autoFocus
                                  value={newEmail}
                                  onChange={(e) => setNewEmail(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && handleAddEmailSubmit(user._id)}
                                />
                                <button className="btn btn-primary" onClick={() => handleAddEmailSubmit(user._id)}>
                                  OK
                                </button>
                                <button className="btn btn-outline-secondary" onClick={() => setEditingUserId(null)}>
                                  <FiX size={12} />
                                </button>
                              </div>
                            ) : (
                              <button
                                className="btn-add-circle shadow-sm"
                                title="Ajouter un email"
                                onClick={() => {
                                  setEditingUserId(user._id);
                                  setNewEmail("");
                                }}
                              >
                                <FiPlus size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="alerts_grid_cell">
                            {user?.alerActive?.length > 0 ? (
                              user.alerActive.map((type) => (
                                <span
                                  key={type}
                                  className={`alert_badge_modern ${getAlertClass(type)}`}
                                  title={`Supprimer l'alerte ${getAlertLabel(type)}`}
                                >
                                  <span className="badge_text">{getAlertLabel(type)}</span>
                                  <button className="alert_close_btn" onClick={() => removeAlertFromUser(user._id, type)}>
                                    <FiX />
                                  </button>
                                </span>
                              ))
                            ) : (
                              <span className="text-muted small italic">Aucune alerte</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              <div className="pagination_wrapper d-flex justify-content-between align-items-center p-3">
                <button
                  className="btn btn-outline-primary btn-sm px-3"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Précédent
                </button>

                <div className="pagination_pages d-none d-md-flex gap-1">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      className={`btn btn-sm ${currentPage === i + 1 ? "btn-primary" : "btn-light"}`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <button
                  className="btn btn-outline-primary btn-sm px-3"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ConfigurationAlert;